/**
 * ProseMirror document ↔ serialized markup string.
 *
 * Rich content (mentions, references, links, etc.) is stored as plain text with
 * embedded tokens defined in markup-to-editor-nodes. These helpers convert
 * between that serialized string and a ProseMirror doc (the in-memory rich
 * structure).
 *
 * - parseMarkupToDoc: parse the markup string into a doc.
 * - serializeDocFromState / serializeDoc: serialize a doc to the markup string.
 */
import { Fragment, Node as ProseMirrorNode } from "prosemirror-model";
import { EditorState } from "prosemirror-state";

import type { BaseLogger } from "@/components/inject";

import {
  fragmentToRichText,
  richTextToFragment,
} from "./markup-to-editor-nodes";
import schema from "./prosemirror/schema";

/** Parse the markup string into a single-paragraph ProseMirror doc (e.g. for Composer). */
export function parseMarkupToDoc(
  markup: string,
  logger: BaseLogger
): ProseMirrorNode {
  const frag = richTextToFragment(markup, logger) ?? Fragment.empty;
  const paragraph = schema.nodes.paragraph.create(null, frag);
  return schema.nodes.doc.create(null, [paragraph]);
}

/** Serialize an editor state's doc to the markup string (blocks joined by "\n"). */
export function serializeDocFromState(state: EditorState): string {
  const parts: ProseMirrorNode[] = [];
  state.doc.forEach((child, _offset, index) => {
    if (index > 0) parts.push(schema.text("\n"));
    if (child.isTextblock) child.content.forEach((n) => parts.push(n));
  });
  return fragmentToRichText(Fragment.fromArray(parts));
}

/** Serialize a ProseMirror doc to the markup string (for display or storage). */
export function serializeDoc(doc: ProseMirrorNode): string {
  const parts: ProseMirrorNode[] = [];
  doc.forEach((child, _offset, index) => {
    if (index > 0) parts.push(schema.text("\n"));
    if (child.isTextblock) child.content.forEach((n) => parts.push(n));
  });
  return fragmentToRichText(Fragment.fromArray(parts));
}
